// src/pages/questionaries/nom035SecIQuestionnaire.jsx

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import nom035SecIQuestions from "../../data/nom035SecIQuestions";
import questionnaireScopeApi from "../../api/questionnaireScopeApi";
import questionnaireApplicationsApi from "../../api/questionnaireApplicationsApi";
import responsesApi from "../../api/responsesApi";

import "./Nom035Questionnaire.css";
import DropdownQuestion from "../../components/questions/dropdownQuestion/dropdownQuestion";
import TextQuestion from "../../components/questions/textQuestion/textQuestion";

const PAGE_SIZE = 5;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isAnswerPresent = (question, value) => {
  if (question.required === false) return true;
  if (value === undefined || value === null) return false;
  if (Array.isArray(value)) return value.length > 0 && value[0] !== "";
  if (typeof value === "string") return value.trim() !== "";
  return true;
};

const getQuestionError = (question, value) => {
  if (!isAnswerPresent(question, value)) return "Pregunta obligatoria";

  if (question.type === "email") {
    const email = String(value || "").trim();
    return EMAIL_REGEX.test(email) ? "" : "Ingresa un correo electrónico válido";
  }

  return "";
};

export default function Nom035SecIQuestionnaire() {
  const { idEmpresa, questionnaire, applicationId } = useParams();
  const navigate = useNavigate();

  const questionsRef = useRef([]);
  const containerRef = useRef(null);
  const [answers, setAnswers] = useState({});
  const [companyName, setCompanyName] = useState("");
  const [companyJobAreas, setCompanyJobAreas] = useState([]);
  const [usesEmployeeCredentials, setUsesEmployeeCredentials] = useState(false);
  const [validatingEmployee, setValidatingEmployee] = useState(false);
  const [configurationLoaded, setConfigurationLoaded] = useState(false);

  const credentialQuestions = useMemo(() => ([
    {
      Component: TextQuestion,
      question: "Número de empleado",
      field: "employee_number",
      required: true,
    },
    {
      Component: TextQuestion,
      question: "Código de empleado",
      field: "employee_code",
      required: true,
    },
  ]), []);

  const jobAreaQuestion = useMemo(
    () => ({
      Component: DropdownQuestion,
      question: "Área de trabajo",
      options: companyJobAreas,
      field: "jobArea",
      required: true,
    }),
    [companyJobAreas]
  );

  const mergedQuestions = useMemo(() => {
    const arr = usesEmployeeCredentials
      ? [
        ...credentialQuestions,
        ...nom035SecIQuestions.filter((question) => question.field !== "email"),
      ]
      : [...nom035SecIQuestions];
    arr.splice(usesEmployeeCredentials ? 4 : 3, 0, jobAreaQuestion);
    return arr;
  }, [credentialQuestions, jobAreaQuestion, usesEmployeeCredentials]);

  const [, setAnsweredState] = useState(
    () => new Array(mergedQuestions.length).fill(false)
  );

  const secIKeys = [
    "seccionI_accidente",
    "seccionI_asaltos",
    "seccionI_actosViolentos",
    "seccionI_secuestro",
    "seccionI_amenazas",
    "seccionI_otroRiesgo",
  ];

  const allSecIAnswered = secIKeys.every(
    (key) => answers[key] != null && answers[key] !== ""
  );
  const allSecINo =
    allSecIAnswered && secIKeys.every((key) => answers[key] === "No");

  const effectiveQuestions = useMemo(() => {
    if (!allSecINo) return mergedQuestions;

    return mergedQuestions.filter(
      (q) =>
        !q.field.startsWith("seccionII_") &&
        !q.field.startsWith("seccionIII_") &&
        !q.field.startsWith("seccionIV_")
    );
  }, [allSecINo, mergedQuestions]);

  const totalQuestions = effectiveQuestions.length;

  useEffect(() => {
    questionsRef.current = questionsRef.current.slice(0, totalQuestions);
  }, [totalQuestions]);

  useEffect(() => {
    (async () => {
      setConfigurationLoaded(false);
      try {
        const scope = await questionnaireScopeApi.getById(idEmpresa);
        setCompanyName(scope.name);
        if (applicationId) {
          const config = await questionnaireApplicationsApi.getNom035Config(applicationId);
          setCompanyJobAreas(config.jobAreas || []);
          setUsesEmployeeCredentials(Boolean(config.usesEmployeeCredentials));
        } else {
          setCompanyJobAreas([]);
          setUsesEmployeeCredentials(false);
        }
      } catch (err) {
        setCompanyJobAreas([]);
        setUsesEmployeeCredentials(false);
        console.error("Error cargando empresa/sucursal:", err);
      } finally {
        setConfigurationLoaded(true);
      }
    })();
  }, [idEmpresa, applicationId]);

  const markQuestionAsAnswered = useCallback((index, field, value) => {
    setAnswers((prev) => ({ ...prev, [field]: value ?? "" }));
    setAnsweredState((prev) => {
      const next = [...prev];
      next[index] = true;
      return next;
    });
  }, []);

  const totalPages = Math.ceil(totalQuestions / PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(0);
  const [pendingFocusField, setPendingFocusField] = useState(null);
  const start = currentPage * PAGE_SIZE;
  const pageQuestions = effectiveQuestions.slice(start, start + PAGE_SIZE);
  const progressPct = Math.round(((currentPage + 1) / totalPages) * 100);

  const focusQuestionByField = useCallback((field) => {
    const globalIdx = mergedQuestions.findIndex((q) => q.field === field);
    if (globalIdx < 0) return false;

    const questionCard = questionsRef.current[globalIdx];
    if (!questionCard) return false;

    questionCard.scrollIntoView({ behavior: "smooth", block: "center" });

    const focusTarget = questionCard.querySelector(
      "input, select, textarea, button, [tabindex]:not([tabindex='-1'])"
    ) || questionCard;

    requestAnimationFrame(() => {
      focusTarget.focus?.({ preventScroll: true });
    });

    return true;
  }, [mergedQuestions]);

  const getFirstInvalidQuestion = useCallback((questionsToCheck) => {
    return questionsToCheck.find((q) => Boolean(getQuestionError(q, answers[q.field])));
  }, [answers]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  useEffect(() => {
    if (!pendingFocusField) return;
    if (focusQuestionByField(pendingFocusField)) {
      setPendingFocusField(null);
    }
  }, [pendingFocusField, currentPage, focusQuestionByField]);

  const handleNext = async () => {
    const firstInvalid = getFirstInvalidQuestion(pageQuestions);
    if (firstInvalid) {
      focusQuestionByField(firstInvalid.field);
      return;
    }

    if (usesEmployeeCredentials && currentPage === 0) {
      setValidatingEmployee(true);
      try {
        await questionnaireApplicationsApi.validateEmployeeCredentials(applicationId, {
          employee_number: answers.employee_number,
          employee_code: answers.employee_code,
        });
      } catch (err) {
        alert(err.response?.data?.message || "No se pudo validar al empleado.");
        return;
      } finally {
        setValidatingEmployee(false);
      }
    }

    setCurrentPage(Math.min(currentPage + 1, totalPages - 1));
  };

  const handlePrev = () => {
    setCurrentPage(Math.max(currentPage - 1, 0));
  };

  const handleSend = async () => {
    const firstInvalid = getFirstInvalidQuestion(effectiveQuestions);
    if (firstInvalid) {
      const targetIndex = effectiveQuestions.findIndex((q) => q.field === firstInvalid.field);
      const targetPage = Math.floor(targetIndex / PAGE_SIZE);

      if (targetPage !== currentPage) {
        setPendingFocusField(firstInvalid.field);
        setCurrentPage(targetPage);
      } else {
        focusQuestionByField(firstInvalid.field);
      }
      return;
    }

    const payload = {
      companyId: idEmpresa,
      questionnaireName: questionnaire,
      answers,
      timestamp: new Date().toISOString(),
      applicationId: applicationId || null,
    };

    try {
      await responsesApi.createNom035SecIResponse(payload);
      navigate("/nom035SecI/results");
    } catch (err) {
      const msg = err.response?.data?.message;
      alert(msg || "Error al enviar respuestas.");
      console.error(err);
    }
  };

  if (!configurationLoaded) {
    return <div className="questionnaire-container">Cargando cuestionario...</div>;
  }

  return (
    <div ref={containerRef} className="questionnaire-container">
      <h2>Cuestionario Nom035 Guia I</h2>
      <p>Empresa: {companyName}</p>
      <p>Lea cuidadosamente cada enunciado y marque la respuesta correspondiente.</p>

      <div className="progress-fill" style={{ width: `${progressPct}%` }} />
      <p className="progress-text">
        Página {currentPage + 1} de {totalPages}
      </p>

      {pageQuestions.map((q) => {
        const globalIdx = mergedQuestions.findIndex((x) => x.field === q.field);
        const QuestionComp = q.Component;
        return (
          <div
            key={q.field}
            ref={(el) => (questionsRef.current[globalIdx] = el)}
            className="question-card"
            tabIndex={-1}
          >
            <QuestionComp
              question={q.question}
              required={q.required}
              type={q.type}
              options={q.options}
              optionA={q.optionA}
              optionB={q.optionB}
              followUp={q.followUp}
              maxRating={q.maxRating}
              hourRange={q.hourRange}
              invertOrder={q.invertOrder}
              uniqueChoice={q.uniqueChoice}
              field={q.field}
              value={answers[q.field] ?? ""}
              updateProgress={(val) => markQuestionAsAnswered(globalIdx, q.field, val)}
            />
          </div>
        );
      })}

      <div className="pagination">
        {currentPage > 0 && (
          <button onClick={handlePrev} className="page-button">
            Anterior
          </button>
        )}
        {currentPage < totalPages - 1 ? (
          <button
            onClick={handleNext}
            className="page-button"
            disabled={validatingEmployee}
          >
            {validatingEmployee ? "Validando..." : "Siguiente"}
          </button>
        ) : (
          <button onClick={handleSend} className="page-button submit-button">
            Enviar
          </button>
        )}
      </div>
    </div>
  );
}
